import React, { useState } from 'react';
import { FileText, Clipboard, Calculator, Loader2 } from 'lucide-react';
import invoiceService from '../../services/invoiceService';
import guaranteeService from '../../services/guaranteeService';
import pdfThumb from '../../assets/pdfthumb.png';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
};

const getStatusColor = (status) => {
  const colors = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700',
    outstanding: 'bg-orange-100 text-orange-700',
    partial: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-green-100 text-green-700',
    accepted: 'bg-green-100 text-green-700',
    declined: 'bg-red-100 text-red-700',
    expired: 'bg-orange-100 text-orange-700',
    converted: 'bg-purple-100 text-purple-700'
  };
  return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-700';
};

const DocumentList = ({ invoices = [], treatmentReports = [], estimates = [], guarantees = [], onViewInvoice }) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingGuaranteeId, setDownloadingGuaranteeId] = useState(null);

  const handleDownloadPDF = async (e, inv) => {
    e.stopPropagation();
    setDownloadingId(inv.id);
    try {
      await invoiceService.downloadPDF(inv.franchisee_id, inv.id, inv.invoice_number);
    } catch (error) {
      console.error('PDF download failed:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadGuaranteePDF = async (e, g) => {
    e.stopPropagation();
    setDownloadingGuaranteeId(g.id);
    try {
      await guaranteeService.downloadPDF(g.franchisee_id, g.id, { customerName: g.customer_name, dateIssued: g.date_issued });
    } catch (error) {
      console.error('Guarantee PDF download failed:', error);
    } finally {
      setDownloadingGuaranteeId(null);
    }
  };

  if (invoices.length === 0 && treatmentReports.length === 0 && estimates.length === 0 && guarantees.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 space-y-1.5">
      {invoices.map(inv => (
        <div
          key={inv.id}
          onClick={(e) => { e.stopPropagation(); onViewInvoice && onViewInvoice(inv); }}
          className="flex items-center gap-2 text-xs py-1.5 px-2 bg-green-50 rounded cursor-pointer hover:bg-green-100 transition-colors"
        >
          <FileText size={15} className="text-green-600 flex-shrink-0" />
          <span className="font-medium text-green-800">{inv.invoice_number}</span>
          <span className="text-green-600">{formatCurrency(inv.total_amount)}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(inv.status)}`}>
            {inv.status}
          </span>
          <button
            onClick={(e) => handleDownloadPDF(e, inv)}
            disabled={downloadingId === inv.id}
            className="ml-auto p-0.5 text-green-600 hover:text-green-800 disabled:opacity-50"
            title="Download PDF"
          >
            {downloadingId === inv.id ? <Loader2 size={15} className="animate-spin" /> : <img src={pdfThumb} alt="PDF" className="h-8 w-auto" />}
          </button>
        </div>
      ))}

      {treatmentReports.map(tr => (
        <div
          key={tr.id}
          className="flex items-center gap-2 text-xs py-1.5 px-2 bg-blue-50 rounded"
        >
          <Clipboard size={15} className="text-blue-600 flex-shrink-0" />
          <span className="font-medium text-blue-800">{tr.report_number}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(tr.status)}`}>
            {tr.status}
          </span>
        </div>
      ))}

      {estimates.map(est => (
        <div
          key={est.id}
          className="flex items-center gap-2 text-xs py-1.5 px-2 bg-teal-50 rounded"
        >
          <Calculator size={15} className="text-teal-600 flex-shrink-0" />
          <span className="font-medium text-teal-800">{est.estimate_number}</span>
          <span className="text-teal-600">{formatCurrency(est.total_amount)}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(est.status)}`}>
            {est.status}
          </span>
        </div>
      ))}

      {guarantees.map(g => (
        <div
          key={g.id}
          className="flex items-center gap-2 text-xs py-1.5 px-2 bg-red-50 rounded"
        >
          <FileText size={15} className="text-red-600 flex-shrink-0" />
          <span className="font-medium text-red-800">{g.guarantee_number}</span>
          <span className="text-red-600">{g.guarantee_years}yr</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(g.status)}`}>
            {g.status}
          </span>
          <button
            onClick={(e) => handleDownloadGuaranteePDF(e, g)}
            disabled={downloadingGuaranteeId === g.id}
            className="ml-auto p-0.5 text-red-600 hover:text-red-800 disabled:opacity-50"
            title="Download PDF"
          >
            {downloadingGuaranteeId === g.id ? <Loader2 size={15} className="animate-spin" /> : <img src={pdfThumb} alt="PDF" className="h-8 w-auto" />}
          </button>
        </div>
      ))}
    </div>
  );
};

export default DocumentList;
